import { QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useSize } from 'ahooks';
import { Card, Tooltip } from 'antd';
import Empty from 'components/Empty';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { useEffect, useRef } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { percentDisplay } from 'views/Dashboard/ImplementData/utils';
import TitleLayout from '../Trend/TitleLayout';
import { isNil } from 'lodash-es';
import { useDashboardPlatformTips } from '../Filter/useFilter';

const Container = styled(Card)`
  margin-top: 16px;
  border-radius: 4px;
  height: 420px;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  .ant-card-head {
    border-bottom-color: var(--fett-border-default);
  }
  .ant-card-head-title {
    color: var(--fett-text-primary);
  }
  width: 100%;
  .ant-card-head {
    .ant-card-head-title {
      .before {
        height: 14px;
        border-left: 4px solid #3370ff;
        display: inline-block;
        margin-right: 4px;
      }
      .anticon {
        margin-left: 4px;
      }
    }
  }
  .ant-card-body {
    display: flex;
    .ant-row {
      height: 50px;
      margin-bottom: 12px;
      background-color: var(--fett-bg-subtle);
      border-radius: 4px;
      align-items: center;
      padding: 0 16px;
      .title {
        color: var(--fett-text-tertiary);
      }
      .num {
        color: var(--fett-text-secondary);
        font-size: 16px;
      }
    }
  }
  .chart {
    position: relative;
    width: 100%;
    .ant-empty {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .bar {
    flex: 1;
    height: 330px;
  }
`;

interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const barOption = (
  value: Dashboard.OrderOverviewInner,
  t: TFnType,
  tips: ReturnType<typeof useDashboardPlatformTips>,
): EChartsOption => {
  const data = value.每日复购 || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  const dataZoom = [
    {
      show: true,
      realtime: true,
      start: 0,
      end: 100,
    },
    {
      type: 'inside',
      realtime: true,
      start: 0,
      end: 100,
    },
  ];
  const {
    下单总量tips,
    platformInfo: { isOnlyMercadoOrOnlyTokopedia },
  } = tips;
  const firstName = isOnlyMercadoOrOnlyTokopedia
    ? t('ViewDashboard.付款订单量')
    : t('ViewDashboard.下单总量');
  return {
    dataZoom: dateRange.length > 10 ? dataZoom : undefined,
    legend: {
      data: [
        {
          name: firstName,
          tooltip: {
            show: Boolean(下单总量tips),
            formatter: 下单总量tips,
          },
        },
        t('ViewDashboard.复购单量'),
        t('ViewDashboard.复购率订单占比'),
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: dateRange,
    },
    yAxis: [
      {
        type: 'value',
      },
      {
        type: 'value',
        name: '%',
        nameTextStyle: {
          align: 'left',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
    series: [
      {
        data: data.map((x) => x.下单总量),
        type: 'bar',
        name: firstName,
        itemStyle: {
          color: '#5B8FF9',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => x.复购单量),
        type: 'bar',
        name: t('ViewDashboard.复购单量'),
        itemStyle: {
          color: '#5AD8A6',
          opacity: 0.85,
        },
      },
      {
        data: data.map((x) => (isNil(x.复购率订单占比) ? undefined : x.复购率订单占比 * 100)),
        type: 'line',
        yAxisIndex: 1,
        smooth: true,

        tooltip: {
          valueFormatter: (value) => `${percentDisplay(Number(value) / 100)}`,
        },
        name: t('ViewDashboard.复购率订单占比'),
        itemStyle: {
          color: '#f6903d',
        },
        lineStyle: {
          color: '#f6903d',
        },
      },
    ],
  };
};

interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const RePurchase = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { currency } = useDashboardCurrency();
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  const tips = useDashboardPlatformTips();

  useEffect(() => {
    if (!value || !chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const options = barOption(value, t, tips);
    chartRef.current.setOption(applyChartTheme(options));
  }, [value, t, currency?.symbol, tips]);
  return (
    <Container
      title={
        <TitleLayout performanceName="REORDER_RATIO">
          <div>
            <span className="before" />
            {t('ViewDashboard.复购情况')}
            <Tooltip
              title={
                tips.复购情况tips?.replaceAll('\\n', '\n') || t('ViewDashboard.复购情况tooltip')
              }
              overlayClassName="whitespace-break-spaces"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </div>
        </TitleLayout>
      }
    >
      <div className="chart">
        <div className="bar" ref={chartDomRef} />
        {value?.每日复购?.length ? null : <Empty />}
      </div>
    </Container>
  );
};

export default RePurchase;
